import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { LoginModalType, PostAuthOptions } from 'types';
import type { Image } from 'types/capi/shared';

import ModalNav from '~/components/Modals/ModalNav';
import {
  type RegistrationEntry,
  RegistrationEntryProvider
} from '~/hooks/useRegistrationInteraction';
import useUser from '~/hooks/useUser';
import { isAppleMobileDevice } from '~/utils/browser';

import AuthDialog from '../AuthDialog';
import DownloadAppFooter from '../DownloadAppFooter';
import ErrorBanner from '../ErrorBanner';
import InitialScreen from '../InitialScreen';
import EmailLogin from '../LoginFlow';
import SignUpCompleteScreen from './SignUpCompleteScreen';
import SignUpScreen from './SignUpScreen';

export enum Stage {
  Error = -1,
  Initial = 0,
  SignUp = 1,
  Complete = 2,
  Login = 3
}

type Props = {
  directSignUp?: boolean;
  hideAppDownloadPrompt?: boolean;
  image?: Image;
  modalType: LoginModalType;
  onDismiss: (options?: { loginSuccessful?: boolean }) => void;
  postAuthOptions?: PostAuthOptions;
  registrationEntry: RegistrationEntry;
};

export default function SignUp({
  onDismiss,
  postAuthOptions,
  image,
  modalType,
  directSignUp = false,
  hideAppDownloadPrompt = false,
  registrationEntry
}: Props) {
  const [stage, setStage] = useState(
    directSignUp ? Stage.SignUp : Stage.Initial
  );
  const [error, setError] = useState<string | null>(null);
  const user = useUser();
  const { t } = useTranslation('common');

  function onBackNavigation() {
    setError(null);
    setStage(stage - 1);
  }

  if (user.isLoggedIn) return null;

  const showAppDownload =
    isAppleMobileDevice(navigator.userAgent) &&
    !hideAppDownloadPrompt &&
    stage === Stage.Initial &&
    !['account', 'purchase', 'profile'].includes(modalType);

  if (stage === Stage.Login) {
    return (
      <RegistrationEntryProvider entry={registrationEntry}>
        <EmailLogin
          onBackNavigation={() => setStage(Stage.Initial)}
          modalType={modalType}
          onDismiss={onDismiss}
          postAuthOptions={postAuthOptions}
        />
      </RegistrationEntryProvider>
    );
  }

  return (
    <RegistrationEntryProvider entry={registrationEntry}>
      <AuthDialog onDismiss={onDismiss}>
        <div
          className="flex w-full flex-col items-center gap-4"
          data-cy="login-modal"
        >
          {stage === Stage.SignUp && (
            <ModalNav
              heading={t('login.stageSignUp.title')}
              onBack={onBackNavigation}
              onDismissHref={modalType === 'profile' ? '/' : undefined}
            />
          )}
          {stage === Stage.Complete && (
            <ModalNav
              heading={t('login.stageComplete.title')}
              onDismissHref={modalType === 'profile' ? '/' : undefined}
            />
          )}
          <Screen
            stage={stage}
            onDismiss={onDismiss}
            setStage={setStage}
            postAuthOptions={postAuthOptions}
            onError={setError}
            image={image}
            modalType={modalType}
          />
          {error && <ErrorBanner error={error} />}
          {showAppDownload && <DownloadAppFooter modalType={modalType} />}
        </div>
      </AuthDialog>
    </RegistrationEntryProvider>
  );
}

export type ScreenProps = {
  image?: Image;
  modalType: LoginModalType;
  onDismiss: (options?: { loginSuccessful?: boolean }) => void;
  onError: (err: string | null) => void;
  postAuthOptions?: PostAuthOptions;
  setStage: (name: Stage) => void;
  stage: Stage;
};

function Screen(props: ScreenProps) {
  const [email, setEmail] = useState('');

  function onLogInClick() {
    props.setStage(Stage.Login);
  }
  function onSignUpClick() {
    props.setStage(Stage.SignUp);
  }

  switch (props.stage) {
    case Stage.Initial:
      return (
        <InitialScreen
          {...props}
          onSignUpClick={onSignUpClick}
          onLogInClick={onLogInClick}
        />
      );
    case Stage.SignUp:
      return <SignUpScreen {...props} setEmail={setEmail} />;
    case Stage.Complete:
      return <SignUpCompleteScreen {...props} email={email} />;
  }
}
